import React, { useState, useRef, useEffect, createContext, useContext, useCallback } from "react";
import { 
  ChevronRight, Usb, Send, Instagram, TreePalm, 
  Disc2, Circle, BatteryFull, BatteryMedium, BatteryLow, BatteryWarning,
  Wifi, WifiHigh, WifiLow, WifiOff, Search, Bell, BellDot 
} from "lucide-react";
import mammoth from "mammoth";
import './App.css';
import EPUBViewer from './EPUBViewer';

// Cursor Manager
const setupCustomCursors = () => {
  const style = document.createElement('style');
  style.textContent = `
    /* Base cursor for all elements */
    * {
      cursor: url('/images/cursor.cur'), auto;
    }

    /* Default cursor override */
    body.custom-cursor-default * {
      cursor: url('/images/cursor.cur'), auto !important;
    }

    /* Pointer cursor */
    body.custom-cursor-pointer * {
      cursor: url('/images/pointer.cur'), pointer !important;
    }

    /* Grab states */
    body.custom-cursor-grab * {
      cursor: url('/images/grab.cur'), grab !important;
    }

    body.custom-cursor-grabbing * {
      cursor: url('/images/grabbing.cur'), grabbing !important;
    }

    /* Text cursor */
    body.custom-cursor-text * {
      cursor: url('/images/text.cur'), text !important;
    }

    /* New move cursor */
    body.custom-cursor-move * {
      cursor: url('/images/move.cur'), move !important;
    }
  `;
  document.head.appendChild(style);

  // Track dragging state
  let isDragging = false;

  document.addEventListener('mouseover', (e) => {
    const target = e.target;
    
    if (isDragging) {
      document.body.classList.add('custom-cursor-grabbing');
      return;
    }

    // Check for draggable elements
    const isDraggable = target.closest('.FolderIcon, .StickyNote, .window-header');
    if (isDraggable) {
      document.body.classList.add('custom-cursor-grab');
      return;
    }

    // Check for text inputs
    if (target.matches('input, textarea, [contenteditable="true"]')) {
      document.body.classList.add('custom-cursor-text');
      return;
    }

    // Check for clickable elements
    if (target.matches('a, button, [role="button"], .cursor-pointer')) {
      document.body.classList.add('custom-cursor-pointer');
      return;
    }

    // Check for move operations (e.g., window dragging)
    if (target.closest('.window-header')) {
      document.body.classList.add('custom-cursor-move');
      return;
    }

    // Reset to default if none match
    document.body.className = 'custom-cursor-default';
  });

  // Existing drag handling
  document.addEventListener('mousedown', () => {
    if (document.body.classList.contains('custom-cursor-grab')) {
      isDragging = true;
      document.body.classList.replace('custom-cursor-grab', 'custom-cursor-grabbing');
    }
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
    document.body.classList.remove('custom-cursor-grabbing', 'custom-cursor-move');
  });

  document.addEventListener('dragstart', (e) => e.preventDefault());
};

// Initialize cursor system
setupCustomCursors();



//image import
const ICON_IMAGES = {
  folder: 'folder.png',
  bin: 'trash.png',
  textedit: 'textedit.png',
  images: 'images.png',
  thesis: 'personal.png',
  settings: 'settings.png',
  document: 'document.png',
  video: 'video.png',
  gourd: 'gourd.png',
  world: 'world.png',
  recipe: 'recipe.png',
  image: 'preview.png',
  projects: 'projects.png',
};
const NOTEPAD_ICON = 'textedit.png';


// Viewport Context
const ViewportContext = createContext({ width: 0, height: 0 });

const ViewportProvider = ({ children }) => {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ViewportContext.Provider value={{ width, height }}>
      {children}
    </ViewportContext.Provider>
  );
};

const useViewport = () => useContext(ViewportContext);



// Start Page Component
const StartPage = ({ onStart }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [overlayLoaded, setOverlayLoaded] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    email: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState("");

  const bgRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    // Preload both images
    const loadImages = async () => {
      await Promise.all([
        new Promise((resolve) => {
          const img = new Image();
          img.src = "/images/gramps.jpg";
          img.onload = () => {
            if (bgRef.current) bgRef.current.style.backgroundImage = `url(${img.src})`;
            resolve();
          };
        }),
        new Promise((resolve) => {
          const img = new Image();
          img.src = "/images/homepage.jpg";
          img.onload = () => {
            if (overlayRef.current) overlayRef.current.style.backgroundImage = `url(${img.src})`;
            setOverlayLoaded(true);
            resolve();
          };
        })
      ]);
      setBackgroundLoaded(true);
    };

    loadImages();
  }, []);

  const handleStartClick = () => {
    setShowPasswordPrompt(true);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput.trim().toLowerCase() === "zgharta") {
      setFadeOut(true);
      setTimeout(() => onStart(), 1000);
    } else {
      setError("Incorrect answer. Try again.");
      setPasswordInput("");
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (Object.values(formData).every(val => val.trim() !== '')) {
      setShowConfirmation(true);
    } else {
      setError("Please fill all fields");
    }
  };

  const handleProceed = () => {
    setFadeOut(true);
    setTimeout(() => onStart(), 1000);
  };

  return (
    <div className="h-screen relative overflow-hidden">
      {/* Base background */}
      <div
        ref={bgRef}
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: backgroundLoaded ? 1 : 0
        }}
      />

      {/* Overlay image */}
      <div
        ref={overlayRef}
        className={`absolute inset-0 transition-all duration-1000 ${fadeOut ? "opacity-0 blur-2xl" : "opacity-100 blur-0"}`}
        style={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: overlayLoaded ? 1 : 0
        }}
      />

      {/* Content */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-100 ${fadeOut ? "opacity-0" : "opacity-100"}`}>
        <div className="text-center">
          {!showPasswordPrompt && !showForm ? (
            <button
              onClick={handleStartClick}
              className="group flex items-center space-x-2 text-zinc-100 hover:text-white transition-colors duration-300 text-5xl font-mono glitch-on-hover"
            >
              <span>press start</span>
              <ChevronRight className="w-6 h-6 transform group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          ) : showForm ? (
            <>
              {!showConfirmation ? (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-52 bg-transparent border-b-2 border-zinc-100 text-green-300 outline-none text-xl text-center"
                  /><br></br>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-52 bg-transparent border-b-2 border-zinc-100 text-green-300 outline-none text-xl text-center"
                  /><br></br>
                  <input
                    type="text"
                    placeholder="Address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-52 bg-transparent border-b-2 border-zinc-100 text-green-300 outline-none text-xl text-center"
                  /><br></br>
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-52 bg-transparent border-b-2 border-zinc-100 text-green-300 outline-none text-xl text-center"
                  /><br></br>
                  <button
                    type="submit"
                    className="mt-4 px-6 py-2 bg-[#4DD0E1] text-white rounded-lg hover:bg-[#26C6DA] transition-colors"
                  >
                    Submit?
                  </button>
                  {error && <p className="text-red-500">{error}</p>}
                </form>
              ) : (
                <div className="text-center max-w-2xl">
                  <p className="text-zinc-100 text-xl mb-6">
                    "Your engagement with this website may be seen as complicity in certain actions."
                    <br />
                    Are you sure you wish to move forward?
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleProceed}
                      className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Proceed At Your Own Risk
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-6 py-2 bg-zinc-500 text-white rounded-lg hover:bg-zinc-600 transition-colors"
                    >
                      Exit Now
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="">
              <form onSubmit={handlePasswordSubmit}>
                <p className="text-zinc-100 text-3xl mb-4 font-mono">Where do I come from?</p>
                <input
                  type="text"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="bg-transparent border-b-2 border-zinc-100 text-green-300 outline-none text-xl text-center mb-4"
                  autoFocus
                />
                {error && <p className="text-red-500 mt-2">{error}</p>}
              </form>
              
              <div className="text-zinc-300 text-sm italic ">Don't know the password?</div>
              <div className="flex justify-center">
              <button
                onClick={() => setShowForm(true)}
                className="group  flex items-center space-x-2 text-[#4DD0E1] hover:text-[#26C6DA] transition-colors duration-300 text-base font-mono glitch-on-hover"
              >
                <span>Let me in anyway!</span>
                <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sticky Note Component
const StickyNote = ({ note, position, onPositionChange, onClose }) => {
  const { width, height } = useViewport();
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [text, setText] = useState(() => {
    const messages = {
      1: `“De type non-gaulois vachement prononcé” — Desproges`,
      2: `“Don’t mistake my generosity for generosity” — White Rose`,
      3: `“Je me suis convertis au sionisme pour des raisons professionnelles” — Dieudonné`
    };
    return messages[note.id] || "Type your note here...";
  });
  const noteRef = useRef(null);
  const textareaRef = useRef(null);
  const [rotation] = useState(() => (Math.random() * 30) - 15); // -15 to +15 degrees

  const getPixelPosition = (pos) => ({
    x: (width * pos.x) / 100,
    y: (height * pos.y) / 100
  });

  const handleMouseDown = (e) => {
    // Changed from checking .note-header to excluding the close button
    if (e.target.closest('button')) return; // Prevent drag when clicking close button
    const rect = noteRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    // Same adjustment for touch events
    if (e.target.closest('button')) return;
    const touch = e.touches[0];
    const rect = noteRef.current.getBoundingClientRect();
    setOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || (e.touches?.[0]?.clientX ?? 0);
    const clientY = e.clientY || (e.touches?.[0]?.clientY ?? 0);
    
    const newX = ((clientX - offset.x) / width) * 100;
    const newY = ((clientY - offset.y) / height) * 100;
    
    onPositionChange({
      x: Math.max(0, Math.min(95, newX)),
      y: Math.max(0, Math.min(95, newY))
    });
  }, [isDragging, offset.x, offset.y, width, height, onPositionChange]);
  
  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, offset, handleMouseMove, handleMouseUp]);

  // Auto-resize textarea when text changes.
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [text]);

  // Recalculate the textarea height after fonts are loaded.
  useEffect(() => {
    document.fonts.ready.then(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
      }
    });
  }, []);

  const pixelPosition = getPixelPosition(position);

  return (
    <div
      ref={noteRef}
      style={{
        position: "absolute",
        left: pixelPosition.x,
        top: pixelPosition.y,
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none"
      }}
      className=" relative group"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div
        className="relative min-w-40 max-w-52 rounded-lg"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.2s ease-in-out' // Optional smooth rotation
        }}
      >
        {/* 
          Note Background Layer:
          This div covers the entire note area and uses clip-path to "cut out"
          the bottom-right corner. That cut-out area will be transparent, letting 
          the parent's (or page’s) background show through.
        */}
        <div
          className="absolute inset-0 rounded-lg   transition-shadow duration-200"
          style={{
            backgroundColor: "rgb(253 230 138 / 85%)",
            // Remove the bottom-right triangle (2rem by 2rem, adjust as needed)
            clipPath: "ellipse(100% 78% at 23% 41%)",
            filter: "drop-shadow(4px 4px 6px rgba(0,0,0,0.2))"
          }}
        ></div>
  
        {/*
          Folded Corner Simulation:
          This element sits exactly where the note is clipped. It simulates
          the crease of a folded corner using a gradient and a subtle shadow.
        */}
              <div
        className="absolute bottom-0 right-0"
        style={{
          width: "2rem",
          height: "2rem",
          clipPath: "polygon(100% 14px, 100% 101%, 0px 97%)",
          WebkitClipPath: "polygon(100% 14px, 100% 101%, 0px 97%)",
          background: "linear-gradient(135deg, rgb(80 79 15 / 83%), rgb(0 0 0 / 0%))",
        }}
      ></div>

  
        {/* Tape Element */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-10 h-4 bg-gray-100/80 rotate-6 rounded-sm shadow-sm z-20"></div>
  
        {/* Content */}
        <div
          className="relative p-2 overflow-visible"
          style={{ fontFamily: "'Reenie Beanie', cursive" }}
        >
          <button
            onClick={onClose}
            className="absolute top-0.5 right-2 text-yellow-800/60 hover:text-yellow-800/90 font-bold z-10"
          >
            ×
          </button>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full bg-transparent resize-none outline-none text-xs sm:text-sm min-h-[2rem] max-h-32 [&::-webkit-scrollbar]:w-0"
            placeholder="Type your note here..."
          />
        </div>
      </div>
    </div>
  );
};

// Folder Icon Component
const FolderIcon = ({ icon, onPositionChange, onClick }) => {
  const { width, height } = useViewport();
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const iconRef = useRef(null);

  const getPixelPosition = (pos) => {
    const iconWidth = 64; 
    const iconHeight = 96;
    return {
      x: (width - iconWidth) * (pos.x / 100),
      y: (height - iconHeight) * (pos.y / 100)
    };
  };

  const handleMouseDown = (e) => {
    const rect = iconRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
    setHasMoved(false); // Reset movement flag on new drag
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const rect = iconRef.current.getBoundingClientRect();
    setOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
    setHasMoved(false); // Reset movement flag on new drag
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || (e.touches?.[0]?.clientX ?? 0);
    const clientY = e.clientY || (e.touches?.[0]?.clientY ?? 0);
    
    const iconWidth = 64;
    const iconHeight = 96;
  
    // Now 100% corresponds to (width - iconWidth)
    const newX = ((clientX - offset.x) / (width - iconWidth)) * 100;
    const newY = ((clientY - offset.y) / (height - iconHeight)) * 100;
    // Check if the movement exceeds the threshold to consider it a drag
    const threshold = 5;
    const originalX = (width * icon.position.x) / 100;
    const originalY = (height * icon.position.y) / 100;
    if (
      Math.abs(clientX - (offset.x + originalX)) > threshold ||
      Math.abs(clientY - (offset.y + originalY)) > threshold
    ) {
      setHasMoved(true);
    }

    onPositionChange(icon.id, {
      x: Math.max(0, Math.min(100, newX)),
      y: Math.max(0, Math.min(100, newY))
    });
  }, [isDragging, offset.x, offset.y, width, height, onPositionChange, icon.id, icon.position]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;
    setIsDragging(false);

    // Only consider it a click if the icon was not moved
    if (!hasMoved) {
      onClick();
    }
  }, [isDragging, hasMoved, onClick]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const pixelPosition = getPixelPosition(icon.position);

  return (
    <div
      ref={iconRef}
      style={{
        position: "absolute",
        left: pixelPosition.x,
        top: pixelPosition.y,
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none"
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className="group"
    >
      <div className="flex flex-col items-center">
        <div className="p-1 rounded-lg bg-transparent group-hover:bg-zinc-800/50 transition-colors">
        <div className="w-16 h-16">
          <img 
            src={`/images/${ICON_IMAGES[icon.type] || 'folder.png'}`}
            alt={`${icon.type.replace(/-/g, ' ')} icon`}
            className="w-full h-full object-contain pointer-events-none"
          />
        </div>
        </div>
        <span className="mt-1 text-stone-100 font-mono text-sm group-hover:text-zinc-300">
          {icon.id.replace("-", " ")}
        </span>
      </div>
    </div>
  );
};


//password modal
const PasswordModal = ({ visible, onSubmit, onCancel }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.toLowerCase() === "from the river to the sea") {
      onSubmit();
      setError(false);
    } else {
      setError(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white/95 p-8 rounded-2xl shadow-xl space-y-4 min-w-[400px]">
        <h2 className="text-2xl font-bold text-zinc-800">Correct This Sentence</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-lg font-medium italic text-zinc-600">
              "From the Window to the Wall"
            </label>
            <input
              type="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#4DD0E1]"
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-sm">Incorrect password, please try again</p>
            )}
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-zinc-600 hover:text-zinc-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4DD0E1] text-white rounded-lg hover:bg-[#26C6DA] transition-colors"
            >
              Unlock
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MenuBar = ({ onNewNote }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isRecording, setIsRecording] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState(100);
  const [wifiStrength, setWifiStrength] = useState(3);
  const [hasNotification, setHasNotification] = useState(false);
  const menuRef = useRef(null);

   // Recording animation
   useEffect(() => {
    const interval = setInterval(() => {
      setIsRecording(prev => !prev);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Battery animation
  useEffect(() => {
    const interval = setInterval(() => {
      setBatteryLevel(prev => prev > 0 ? prev - 1 : 100);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // WiFi animation
  useEffect(() => {
    const interval = setInterval(() => {
      setWifiStrength(prev => Math.floor(Math.random() * 4));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Notification animation
  useEffect(() => {
    const interval = setInterval(() => {
      setHasNotification(Math.random() > 0.7);
    }, 6000);
    return () => clearInterval(interval);
  }, []);
  
  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, []);

  // Close menu if click happens outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date) => {
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
  };

  const getBatteryIcon = () => {
    if (batteryLevel > 75) return <BatteryFull className="w-5 h-5 text-green-500" />;
    if (batteryLevel > 50) return <BatteryMedium className="w-5 h-5 text-yellow-500" />;
    if (batteryLevel > 20) return <BatteryLow className="w-5 h-5 text-orange-500" />;
    return <BatteryWarning className="w-5 h-5 text-red-500" />;
  };

  const getWifiIcon = () => {
    switch(wifiStrength) {
      case 3: return <Wifi className="w-5 h-5 text-blue-500" />;
      case 2: return <WifiHigh className="w-5 h-5 text-green-500" />;
      case 1: return <WifiLow className="w-5 h-5 text-yellow-500" />;
      default: return <WifiOff className="w-5 h-5 text-red-500" />;
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gray-500 bg-clip-padding backdrop-filter backdrop-blur-3xl bg-opacity-100">
      <div className="flex justify-between items-center px-4 py-0.5">
        {/* Left Side */}
        <div className="flex items-center space-x-4 font-sans text-[13px]">
          <div className="flex items-center space-x-1 text-white">
            <Usb className="w-5 h-5 mx-2" />
            <span className="font-bold">FIND MY THESIS</span>
          </div>

          {/* Desktop Menu */}
          <div className="relative sm:block" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="px-3 py-0.5 text-white hover:bg-white hover:bg-opacity-20 rounded font-semibold"
            >
              File
            </button>
            {menuOpen && (
              <div className="absolute top-full left-0 mt-1 bg-gray-500 rounded bg-clip-padding backdrop-filter backdrop-blur-3xl
               bg-opacity-90 min-w-[160px] border border-gray-300/30">
                <div className="px-3 py-2 text-xs text-gray-400">New Window</div>
                <div className="px-3 py-2 text-xs text-gray-400">Open...</div>
                <div className="border-t border-zinc-700 my-1" />
                <button
                  onClick={onNewNote}
                  className="w-full text-left px-3 py-2 text-xs text-white hover:bg-gray-700 rounded"
                >
                  New Note
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-3">
            {/* Recording */}
            <div className="flex items-center">
              {isRecording ? (
                <Disc2 className="sm:w-5 sm:h-5 w-4 h-4 text-red-500 animate-pulse" />
              ) : (
                <Circle className="sm:w-5 sm:h-5 w-4 h-4" />
              )}
            </div>

            {/* Battery */}
            <div className="hidden sm:flex items-center space-x-1">
            <span className="text-xs font-mono text-gray-300">{batteryLevel}%</span>
              {getBatteryIcon()}
            </div>

            {/* WiFi */}
            <div className="hidden sm:flex items-center">
              {getWifiIcon()}
            </div>

            {/* Search */}
            <div className="hidden sm:flex items-center">
              <Search className="w-5 h-5 text-gray-300" />
            </div>

            {/* Notifications */}
            <div className="hidden sm:flex relative">
              {hasNotification ? (
                <BellDot className="w-5 h-5 text-green-300 animate-bell" />
              ) : (
                <Bell className="w-5 h-5 text-gray-300" />
              )}
            </div>
          </div>

          {/* Time/Date */}
          <div className="sm:text-xs text-[0.5rem] text-gray-300 font-semibold font-sans ml-4">
            {formatDate(currentTime)}
          </div>
        </div>
      </div>
    </div>
  );
};

const GameMenuWindow = ({ onClose }) => {
  const animationFrame = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  
  // play music
  useEffect(() => {
    const audio = new Audio('/assets/tarot.mp3');
    audio.loop = true;
    // Attempt to play audio. Note that some browsers require user interaction to play sound.
    audio.play().catch((err) => {
      console.error("Audio playback failed:", err);
    });

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);


 // Parallax animation using transforms
 useEffect(() => {
  const animate = (time) => {
    const speed = 0.00025; // Adjust this value to control speed
    const movementRange = 20; 
    
    const x = Math.cos(time * speed) * movementRange;
    const y = Math.sin(time * speed * 0.7) * movementRange;
    
    setOffset({ x, y });
    animationFrame.current = requestAnimationFrame(animate);
  };

  animationFrame.current = requestAnimationFrame(animate);
  return () => cancelAnimationFrame(animationFrame.current);
}, []);

return (
  <div className="fixed inset-0 z-[9999] overflow-hidden">
    {/* Animated Background */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: "url('/images/tarot.webp')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: `translate(
          ${offset.x}px, 
          ${offset.y}px
        ) scale(1.1)`, // Scale ensures edges stay covered
        transition: 'transform 0.1s linear', // Faster transition
      }}
    />

      {/* Menu Content */}
      <div className="flex flex-col items-center justify-center w-full h-full relative z-10 mt-24">
  <h1
    className="text-8xl font-bold text-red-400 mb-10 crt-text"
    style={{ 
      fontFamily: "DungeonFont",
      textShadow: `
        0 0 2px #ff0000,
        2px 2px 0 #8b0000,
        -1px -1px 0 #8b0000
      `
    }}
  >
    TAROT
  </h1>
  <div className="flex flex-col items-center space-y-5 crt-screen">
    {/* Start Game Button */}
    <button
      className="ps1-button relative w-64 px-6 py-4 bg-[#3a0f0f] hover:bg-[#4a1818] 
                border-2 border-[#5a2525] skew-x-[-5deg] skew-y-[1deg]
                transition-all duration-75 hover:skew-x-[-7deg] hover:skew-y-[2deg]
                shadow-crt hover:shadow-crt-hover"
      style={{ fontFamily: "DungeonFont" }}
    >
      <div className="absolute inset-0 bg-repeat opacity-20 crt-noise" />
      <span className="relative text-red-300 text-3xl tracking-wide">
        <span className="text-red-500">▸</span> Start Game
      </span>
    </button>

    {/* Load Game Button */}
    <button
      className="ps1-button relative w-64 px-6 py-4 bg-[#3a0f0f] hover:bg-[#4a1818] 
                border-2 border-[#5a2525] skew-x-[3deg] skew-y-[-1deg]
                transition-all duration-75 hover:skew-x-[5deg] hover:skew-y-[-2deg]
                shadow-crt hover:shadow-crt-hover"
      style={{ fontFamily: "DungeonFont" }}
    >
      <div className="absolute inset-0 bg-repeat opacity-20 crt-noise" />
      <span className="relative text-red-300 text-3xl tracking-wide">
        <span className="text-red-500">▸</span> Load Game
      </span>
    </button>

    {/* Settings Button */}
    <button
      className="ps1-button relative w-64 px-6 py-4 bg-[#3a0f0f] hover:bg-[#4a1818] 
                border-2 border-[#5a2525] skew-x-[-2deg] skew-y-[0deg]
                transition-all duration-75 hover:skew-x-[-4deg] hover:skew-y-[1deg]
                shadow-crt hover:shadow-crt-hover"
      style={{ fontFamily: "DungeonFont" }}
    >
      <div className="absolute inset-0 bg-repeat opacity-20 crt-noise" />
      <span className="relative text-red-300 text-3xl tracking-wide">
        <span className="text-red-500">▸</span> Settings
      </span>
    </button>

    {/* Exit Button */}
    <button
      onClick={onClose}
      className="ps1-button relative w-64 px-6 py-4 bg-[#3a0f0f] hover:bg-[#4a1818] 
                border-2 border-[#5a2525] skew-x-[0deg] skew-y-[1deg]
                transition-all duration-75 hover:skew-x-[2deg] hover:skew-y-[3deg]
                shadow-crt hover:shadow-crt-hover"
      style={{ fontFamily: "DungeonFont" }}
    >
      <div className="absolute inset-0 bg-repeat opacity-20 crt-noise" />
      <span className="relative text-red-300 text-3xl tracking-wide">
        <span className="text-red-500">▸</span> Exit
      </span>
    </button>
  </div>
</div>
    </div>
  );
};

const groupWindowsByType = (windows) => {
  return windows.reduce((groups, window) => {
    const key = window.type;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(window);
    return groups;
  }, {});
};

// Window Component
const Window = ({ windowData, onMove, onMaximize, onMinimize, onClose, onOpenWindow }) => {
  const { width, height } = useViewport();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedImage, setSelectedImage] = useState(null);
  const windowRef = useRef(null);
  const { id, type, position, isMaximized  } = windowData;
  const docxRef = useRef(null);

useEffect(() => {
  if (type === 'document' && id.endsWith('.docx')) {
    const convertDocx = async () => {
      try {
        const response = await fetch(`/assets/${id}`);
        const arrayBuffer = await response.arrayBuffer();
        
        const result = await mammoth.convertToHtml({ arrayBuffer });
        docxRef.current.innerHTML = result.value;
        
        //  basic styling for docx elements
        const style = document.createElement('style');
        style.innerHTML = `
          .document-content table {
            border-collapse: collapse;
            margin: 1rem 0;
          }
          .document-content td, .document-content th {
            border: 1px solid #ddd;
            padding: 8px;
          }
        `;
        docxRef.current.appendChild(style);
      } catch (error) {
        console.error('Error converting DOCX:', error);
        docxRef.current.innerHTML = '<p>Error loading document</p>';
      }
    };

    convertDocx();
  }
}, [id, type]);

  const getPixelPosition = (pos) => ({
    x: (width * pos.x) / 100,
    y: (height * pos.y) / 100
  });

  const handleMouseDown = (e) => {
    if (isMaximized) return;
    if (!e.target.closest(".window-header")) return;
    
    const clientX = e.clientX || (e.touches?.[0]?.clientX ?? 0);
    const clientY = e.clientY || (e.touches?.[0]?.clientY ?? 0);

    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || (e.touches?.[0]?.clientX ?? 0);
    const clientY = e.clientY || (e.touches?.[0]?.clientY ?? 0);
    
    const newX = ((clientX - dragOffset.x) / width) * 100;
    const newY = ((clientY - dragOffset.y) / height) * 100;
    
    onMove({
      x: Math.max(0, Math.min(95, newX)),
      y: Math.max(0, Math.min(95, newY))
    });
  }, [isDragging, dragOffset.x, dragOffset.y, width, height, onMove]);
  
  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleMaximize = () => {
    onMaximize(!isMaximized);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleMouseUp);
    };
    }, [isDragging, handleMouseMove, handleMouseUp]);

  const getWindowDimensions = () => {
    if (isMaximized) return { height: 'calc(100dvh - 140px)', maxHeight: 'none', top: '30px' };
    if (type === 'document') return { height: '80vh', maxHeight: '90vh' };
    if (type === 'video') return { width: '60vh', height: '50vh' };
    if (type === 'image') return { height: 'auto', maxHeight: '90vh' };
    if (id === "images") return { height: '80vh', maxHeight: '70vh' };
    if (id === "settingsdock") return { height: 'auto', width: 'auto', maxWidth: '60vh' };
    if (type === "recipe"|| type === "world" || type === "gourd") return { height: '60vh',  maxWidth: '87vh' };
    if (id === "emaildock") return { height: 'auto', width: '40vh' };
    if (id === "Projects") return { height: 'auto', maxWidth: '40vh'}
        return { height: 'auto', maxHeight: '70vh' };
  };
  
  const windowDimensions = getWindowDimensions();

    //email
    const email = 'melissa.ghazale@etu.hesge.ch';
    const subject = encodeURIComponent('Contact from Desktop App');
    const body = encodeURIComponent('Hello Baha,\n\nI wanted to reach out regarding...\n\n');
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

  return (
      <div
        ref={windowRef}
        style={{
          position: "absolute",
          left: isMaximized ? 0 : getPixelPosition(position).x,
          top: isMaximized ? 0 : getPixelPosition(position).y,
          width: isMaximized ? '100vw' : '90vw',
          maxWidth: isMaximized ? 'none' : '800px',
          ...windowDimensions,
          cursor: isDragging ? "grabbing" : "default",
        }}
        className={`${ type === 'gameMenu' ? 'z-50' : ''} ${(id === 'read_me' || type === 'mail') ? 'bg-white' : 'bg-zinc-900'} rounded-lg shadow-2xl border border-zinc-800 z-20 flex flex-col`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
                {type !== 'gameMenu' && (
        <div className="window-header flex items-center px-4 py-2 border-b border-zinc-800 cursor-grab active:cursor-grabbing">
            <div className="flex space-x-2">
              <button
                className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer relative group flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                title="Close window"
              >
                <span
                  className="absolute opacity-0 group-hover:opacity-100 text-red-900 font-bold text-[10px] mb-[1px]"
                  style={{ lineHeight: "0" }}
                >
                  ×
                </span>
              </button>
              <button
                  className="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-600 transition-colors cursor-pointer"
                  title={windowData.isMinimized ? "Restore window" : "Minimize window"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMinimize();
                  }}
                />
              <button
                className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMaximize();
                }}
                title={isMaximized ? "Restore window" : "Maximize window"}
              />
            </div>
            <div className="flex-1 text-center">
              <span className="text-zinc-400 text-sm font-mono">
                {id}
              </span>
            </div>
          </div>
)}
          <div className={`${(id === 'images' || type === 'document' || type === 'image' || id === 'Worldometer' || id === 'follow the drinking gourd' || type === 'video' || id === 'settingsdock' || id === 'Recipe Book') ? '' : 'p-8'} ${id === 'read_me' ? 'bg-white' : ''} flex-1 overflow-y-auto`}>
          {type === 'document' ? (
              <div className="h-full w-full bg-white flex flex-col">
              {id.endsWith('.pdf') ? (
                <iframe
                  src={`/assets/${id}`}
                  className="w-full flex-1 border-none"
                  title="PDF Viewer"
                />
              ) : (
                <div 
                  className="p-4 prose max-w-none flex-1 overflow-auto"
                  ref={docxRef}
                />
              )}
            </div>
          ) : id === "images" ? (
            <div className="flex h-full">
              {/* Left panel - Image list */}
              <div className="w-1/4 border-r border-zinc-800 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-600">
                <div className="p-2 space-y-2">
        {[
          'desktopexplained.jpg',
          ...Array.from({length: 8}, (_, i) => `${i + 1}.png`)
        ].map((file, index) => (
          <div
            key={file}
            onClick={() => setSelectedImage(file === 'desktopexplained.jpg' ? file : parseInt(file))}
                        className={`flex flex-col items-center p-2 rounded cursor-pointer ${
                          selectedImage === file 
                            ? 'bg-zinc-800' 
                            : 'hover:bg-zinc-800/50'
                        }`}
                      >
                        <img
                          src={`/images/${ICON_IMAGES.image}`}
                          className="w-8 h-8 mr-2"
                          alt="Preview"
                        />
                        <span className="text-zinc-400 font-mono text-sm">
              {file === 'images/desktopexplained.jpg' 
                ? file 
                : `${file.split('.')[0]}.png`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

          {/* Right panel - Image preview */}
          <div className="w-3/4 h-full flex items-center justify-center bg-zinc-900/50">
            {selectedImage ? (
              <img
                src={`/images/${
                  typeof selectedImage === 'number' 
                    ? `${selectedImage}.png` 
                    : selectedImage
                }`}
                className="max-h-full max-w-full object-contain"
                alt="Preview"
              />
            ) : (
              <div className="text-zinc-600 font-mono text-sm">
                Select an image to preview
              </div>
            )}
          </div>
        </div>
        ) : id === "recycle-bin" ? (
          <div className="grid grid-cols-2 gap-4 p-4">
          {/* PDF File */}
          {['biography.pdf', 'petition.pdf', 'emails 1.docx', 'emails2.docx', 'AFSC_Speak_Truth_To_Power.pdf'].map((file, index) => (
          <div key={file} className="flex flex-col items-center cursor-pointer group"
            onClick={() => onOpenWindow({
              id: file,
              type: "document",
              position: { x: 15, y: 15 },
            })}>
            <div className="w-16 h-16">
              <img 
                src={`/images/${file.endsWith('.pdf') ? 'document' : 'word'}.png`}
                className="w-full h-full object-contain"
                alt="PDF icon"
              />
            </div>
            <span className="mt-1 text-zinc-400 font-mono text-sm group-hover:text-zinc-300 text-center">
              {file}
            </span>
          </div>
          ))}
  
          {/* Images */}
          {['pic.jpeg', 'passport.jpg'].map((file, index) => (
            <div key={file} className="flex flex-col items-center cursor-pointer group"
              onClick={() => onOpenWindow({
                id: file,
                type: "image",
                position: { x: 15 + index*15, y: 15 },
                content: `/images/${file}`
              })}>
              <div className="w-16 h-16">
                <img 
                  src={`/images/${ICON_IMAGES.image}`}
                  className="w-full h-full object-contain"
                  alt="Image icon"
                />
              </div>
              <span className="mt-1 text-zinc-400 font-mono text-sm group-hover:text-zinc-300 text-center">
                {file}
              </span>
            </div>
          ))}
          </div>
        ) : id === "read_me" ? (
        <div className="text-black font-mono text-sm space-y-4">
          <div className="flex items-center mb-4">
            <img src={`/images/${NOTEPAD_ICON}`} className="w-8 h-8 mr-2" alt="Notepad" />
            <h1 className="text-xl font-bold">FIND MY THESIS</h1>
          </div>
          <div className="space-y-4">
          {`
            The structure of my thesis—comprising a physical book, a critical essay, and an interactive website—reflects a deliberate strategy to engage with institutional requirements while challenging their boundaries. The physical book, "Where I Come From," embodies a personal and semi-localized narrative, offering a tangible entry point into the work. The critical essay, "How to Say Things Without Saying," operates as a reflective and analytical guide to the strategies of coded language, obfuscation, and resistance to censorship. These components are interlinked through the website "Find My Thesis," which serves as both a meta-textual layer and a gateway, inviting readers to engage with the project in an active, participatory manner.

            This approach stems from a necessity—rooted in distrust of the institutional framework that underpins the HES-SO requirements. While I respect the CCC program and its pedagogical intentions, I have lost faith in the broader institution's ability to foster genuine critical inquiry without imposing forms of surveillance and censorship. Consequently, my thesis complies with the formal expectations, yet subverts them by embedding layers of inaccessibility that demand effort, curiosity, and complicity from the reader. Only those willing to engage in research, decoding, and critical thinking—essentially becoming accomplices in the act of subversion—will access the work’s full meaning.

            This deliberate obfuscation reflects a refusal to present my ideas bluntly, knowing they might otherwise be dismissed, suppressed, or sanitized. It transforms the thesis into a game—not for entertainment, but as a mechanism to defy and critique the conditions of its own creation. By requiring the reader to actively participate in uncovering its content, the work ensures that its core ideas remain protected from institutional control while fostering a deeper engagement with the processes of decoding and meaning-making. This tension between compliance and resistance lies at the heart of the project, illustrating the power of subtext and indirect communication to navigate oppressive structures while preserving autonomy and integrity.


            P.S.: I am sorry if this is not what you were expecting, you can scroll through my desktop, maybe you’ll find what you’re looking for
            `.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph.trim().replace(/\n/g, ' ')}
              </p>
            ))}
          </div>
        </div>
      
      ) : type === 'image' ? (
        <div className="h-full w-full flex items-center justify-center bg-zinc-900" onClick={() => {
          if (id === 'ALL THE REASONS WHY THE TOWER MUST FALL.jpg') {
            onOpenWindow({
              id: 'tower-fall-game',
              type: "gameMenu",
              position: { x: 0, y: 0 },
              isMaximized: true,
              zIndex: 9999
            });
          }
        }}>
          <img
            src={windowData.content}
            className="max-h-full max-w-full object-contain"
            alt="Full size preview"
          />
        </div>
      ) : type === 'video' ? (
        <div className="h-full w-full flex items-center justify-center bg-zinc-900">
          <iframe
            width="100%"
            height="100%"
            src={id === 'mail' ? windowData.content : `https://www.youtube.com/embed/${windowData.content}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        
      ) : id === 'emaildock' ? (
        <div className="h-full w-full flex flex-col items-center justify-center space-y-6 bg-white">
          <h2 className="text-2xl font-bold text-black">Send me a message</h2>
          <button
            onClick={() => window.open(gmailUrl, '_blank')}
            className="flex flex-col items-center space-y-2 group text-black hover:text-green-300 transition-colors"
          >
            <Send className="w-12 h-12" />
            <span className="font-mono text-sm">Compose Email</span>
          </button>
        </div>
  ) : type === 'gourd' ? (
    <div className="h-full w-full flex items-center justify-center bg-white ">
      <iframe
        width="100%"
        height="100%"
        src="https://www.followthedrinkinggourd.org/"
        title="Follow the Drinking Gourd"
        frameBorder="0"
        allowFullScreen
      ></iframe>
    </div>
  ) : type === 'world' ? (
    <div className="h-full w-full flex items-center justify-center bg-zinc-900">
      <iframe
        width="100%"
        height="100%"
        src="https://www.worldometers.info/"
        title="Worldometer"
        frameBorder="0"
        allowFullScreen
      ></iframe>
    </div>
) : id === 'settingsdock' ? (
  
  <div className="h-full w-full flex flex-col items-center justify-center space-y-6 bg-zinc-900 p-8">
    <img 
      src="/images/melissasettings.webp" 
      className="w-32 h-32 rounded-full object-cover border-4 border-zinc-700"
      alt="Profile"
    />
    <div className="text-center space-y-2">
      <h2 className="text-2xl font-bold text-zinc-200">Melissa Ghazale</h2>
      <p className="text-zinc-400 font-mono w-72">
      writing, videography, performance, and other worthy disappointments.
      </p>
    </div>
    <div className="flex space-x-4">
      <a 
        href="https://www.instagram.com/melissaghazale/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-zinc-400 hover:text-pink-600 transition-colors"
      >
        <Instagram className="w-8 h-8" />
      </a>
      <a 
        href="https://linktr.ee/mmmalice" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-zinc-400 hover:text-blue-600 transition-colors"
      >
        <TreePalm className="w-8 h-8" />
      </a>
    </div>
  </div>
) : type === 'recipe' ? (
  <div className="h-full w-full bg-white">
  <EPUBViewer file="/assets/cookbook.epub" />
</div>
//   <div className="h-full w-full bg-white flex">
//   <iframe
//     src="http://localhost:8888/.netlify/functions/proxy?url=https://linktr.ee/mmmalice"
//     className="flex-1 border-0"
//     title="Linktree Profile"
//     referrerPolicy="no-referrer"
//     sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
//     loading="lazy"
//   />
// </div>
) : type === 'gameMenu' ? (
  <GameMenuWindow onClose={onClose} />
) : (
        <div className="text-zinc-400 font-mono">
          {id.startsWith("thesis") ? "Nothing to see here..." : `Contents of ${id} folder`}
        </div>
      )}
    </div>
    </div>
  );
};

// Bird Flock Component
const BirdFlock = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 10 }).map((_, i) => (
        <Bird key={`bird-${i}`} index={i} />
      ))}
    </div>
  );
};

const Bird = ({ index }) => {
  const [visible, setVisible] = useState(false);
  const [direction, setDirection] = useState('right');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const randomInterval = setInterval(() => {
      // Randomly choose direction
      setDirection(Math.random() > 0.5 ? 'right' : 'left');
      // Adjust offset range if needed (e.g. -10vh to +10vh)
      setOffset(Math.random() * 20 - 10);
      setVisible(true);
      setTimeout(() => setVisible(false), 5000);
    }, 7000 + Math.random() * 3000 + index * 200);

    return () => clearInterval(randomInterval);
  }, [index]);

  return (
    // Move birds higher up (e.g., starting at 10% from the top rather than 50%)
    <div
      className={`fixed transition-opacity duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
      style={{ top: `${10 + offset}%` }}
    >
      <img
        src="/images/bird.gif"
        // Use the correct animation class based on direction.
        className={`w-16 h-auto ${direction === 'right' ? 'animate-flock-right' : 'animate-flock-left'}`}
        style={{ animationDelay: `${index * 0.1}s` }}
        alt="Flying bird"
      />
    </div>
  );
};

// // Deer Herd Component
// const DeerHerd = () => {
//   return (
//     <div className="fixed inset-0 pointer-events-none z-50">
//       {Array.from({ length: 4 }).map((_, i) => (
//         <Deer key={`deer-${i}`} index={i} />
//       ))}
//     </div>
//   );
// };

// const Deer = ({ index }) => {
//   const [visible, setVisible] = useState(false);
//   const [direction, setDirection] = useState('left');
//   const [verticalOffset, setVerticalOffset] = useState(10);

//   useEffect(() => {
//     const randomInterval = setInterval(() => {
//       // Randomly choose a direction for each cycle:
//       setDirection(Math.random() > 0.5 ? 'right' : 'left');
//       // Randomize vertical offset (e.g., between 5% and 15% from the bottom)
//       setVerticalOffset(5 + Math.random() * 10);
//       setVisible(true);
//       // Match visible time to animation duration
//       setTimeout(() => setVisible(false), 12000);
//     }, 5000 + Math.random() * 2000 + index * 300);

//     return () => clearInterval(randomInterval);
//   }, [index]);

//   return (
//     <div
//       className={`fixed transition-opacity duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
//       // Use the randomized vertical offset to add organic variation.
//       style={{ bottom: `${verticalOffset}%` }}
//     >
//       <img
//         src="/images/deer.gif"
//         // Choose the appropriate animation class based on the direction.
//         className={`w-32 h-auto ${direction === 'right' ? 'animate-deer-right' : 'animate-deer-left'}`}
//         style={{ animationDelay: `${index * 0.3}s` }}
//         alt="Running deer"
//       />
//     </div>
//   );
// };

const Drone = () => {
  const [visible, setVisible] = useState(false);
  const [direction, setDirection] = useState('right');
  const [topOffset, setTopOffset] = useState(10);

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(prev => prev === 'right' ? 'left' : 'right');
      setTopOffset(10 + Math.random() * 30); // Random between 10% and 40%
      setVisible(true);
      setTimeout(() => setVisible(false), 8000);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`fixed transition-opacity duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        top: `${topOffset}%`,
        // Remove manual left positioning
      }}
    >
      <img
        src="/images/drone.gif"
        className={`w-32 h-auto ${
          direction === 'right' 
            ? 'animate-drone-right' 
            : 'animate-drone-left'
        }`}
        style={{
          transform: direction === 'left' ? 'scaleX(-1)' : 'none',
          position: 'fixed'
        }}
        alt="Surveillance drone"
      />
    </div>
  );
};

// Desktop Component
const Desktop = ({ stickyNotes, setStickyNotes }) => {
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalText, setTerminalText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isAnswered, setIsAnswered] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [requestedWindow, setRequestedWindow] = useState(null);
  const [terminalTimeout, setTerminalTimeout] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const countdownRef = useRef(null);

  const [desktopIcons, setDesktopIcons] = useState([
    { id: "images", type: "images", position: { x: 5, y: 5 } },
    { 
      id: "Projects", 
      type: "projects", 
      position: { x: 5, y: 20 },
      url: "https://328001.cargo.site" // Add URL property
    },
    { id: "follow the drinking gourd", type: "gourd", position: { x: 85, y: 5 } },
    { id: "Worldometer", type: "world", position: { x: 90, y: 35 } },
    { id: "Recipe Book", type: "recipe", position: { x: 90, y: 50 }},
    { id: "read_me", type: "textedit", position: { x: 5, y: 35 } },
    { id: "Manifesto Of Disobedience", type: "thesis", position: { x: 7, y: 65 }, action: "terminal" },
    { id: "ALL THE REASONS WHY THE TOWER MUST FALL.jpg", type: "image", position: { x: 5, y: 80 }, content: "/images/ALL THE REASONS WHY THE TOWER MUST FALL.jpg" },
    { id: "how to say things without saying them.pdf", type: "document",  position: { x: 20, y: 55 } },
    { id: "recycle-bin", type: "bin", position: { x: 90, y: 65 } },
  ]);

  const [openWindows, setOpenWindows] = useState([]);

  const handleIconPositionChange = (iconId, newPosition) => {
    setDesktopIcons(prev => prev.map(icon => 
      icon.id === iconId ? { ...icon, position: newPosition } : icon
    ));
  };

  const terminalContainerRef = useRef(null);

  useEffect(() => {
    if (terminalContainerRef.current) {
      terminalContainerRef.current.scrollTop = terminalContainerRef.current.scrollHeight;
    }
  }, [terminalText]);

  const handleOpenWindow = (icon) => {
    // Handle URL links (eg: Projects)
    if (icon.url) {
      window.open(icon.url, '_blank');
      return;
    }

      // Handle terminal action
      if (icon.action === "terminal") {
        // Reset timer state when opening
        setTimeLeft(180);
        setShowTerminal(true);
        return;
      }

    if (icon.id === "how to say things without saying them.pdf") {
      setRequestedWindow(icon);
      setShowPasswordModal(true);
      return;
    }
  
    setOpenWindows(prev => {
      const existingWindow = prev.find(w => w.id === icon.id);
      const viewportWidth = window.innerWidth;
      const defaultWidth = viewportWidth * 0.9; // 90vw
  
      // Calculate safe position
      let safePosition = icon.position || { x: 7, y: 15 };
      
      // If position is on the right half of screen, adjust to prevent overflow
      if (safePosition.x > 50) {
        safePosition = { 
          x: Math.max(5, 100 - (defaultWidth / viewportWidth * 100) - 5), // Calculate max left position
          y: safePosition.y 
        };
      }
  
      if (existingWindow) {
        return prev.map(window => 
          window.id === icon.id 
            ? { ...window, isMinimized: false }
            : window
        );
      }
  
      const baseConfig = {
        id: icon.id,
        type: icon.type,
        position: safePosition,
        isMaximized: false,
        isMinimized: false,
        previousState: null
      };
  
      if (icon.type === 'image' || icon.type === 'video') {
        return [...prev, { ...baseConfig, content: icon.content }];
      }
      
      return [...prev, baseConfig];
    });
  };

  const handleWindowMove = (windowId, newPosition) => {
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, position: newPosition } : window
    ));
  };

  const handleWindowMaximize = (windowId, maximized) => {
    setOpenWindows(prev => prev.map(window => {
      if (window.id === windowId) {
        return {
          ...window,
          isMaximized: maximized,
          previousState: maximized ? {
            position: window.position,
            size: { width: '90vw', maxWidth: '800px' }
          } : null
        };
      }
      return window;
    }));
  };

  const handleWindowMinimize = (windowId, minimized) => {
    setOpenWindows(prev => prev.map(window => 
      window.id === windowId ? { ...window, isMinimized: minimized } : window
    ));
  };

  const handleWindowClose = (windowId) => {
    setOpenWindows(prev => prev.filter(window => window.id !== windowId));
  };

  const handlePasswordSubmit = () => {
    setShowPasswordModal(false);
    setOpenWindows(prev => {
      const exists = prev.find(w => w.id === requestedWindow.id);
      if (!exists) {
        return [...prev, {
          id: requestedWindow.id,
          type: requestedWindow.type,
          position: requestedWindow.position || { x: 7, y: 15 },
          isMaximized: false,
          previousState: null
        }];
      }
      return prev;
    });
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setRequestedWindow(null);
  };


    // this is here cause code is bad, should reuse stickynote one
    const handleNewNote = () => {
      setStickyNotes(notes => [
        ...notes,
        {
          id: Date.now(),
          position: {
            x: Math.random() * 80 + 10,
            y: Math.random() * 80 + 10
          }
        },
      ]);
    };

    useEffect(() => {
      if (showTerminal) {
        const message = `  Find the missing D: \n \n   An apartheid state does not have the right to exist, \n   therefore it does not have the right to ..... itself \n \nAnswer: `;
        let index = 0;
    
        // Start typing after 3 seconds
        const startTyping = () => {
          const animateText = () => {
            if (index < message.length) {
              setTerminalText(prev => prev + message[index]);
              index++;
              setTimeout(animateText, 20);
            }
          };
    
          animateText();
        };
    
        // Delay the text animation 
        const delay = setTimeout(startTyping, 2000);
    
        // Cleanup timeout when component unmounts or showTerminal changes
        return () => clearTimeout(delay);
      }
    }, [showTerminal]);

    // Countdown timer for terminal
    // Close terminal after 5 minutes
    // and reset countdown
    useEffect(() => {
      if (showTerminal) {
        countdownRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(countdownRef.current);
              setShowTerminal(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
  
        // Set timeout to close terminal after 5 minutes
        const timeout = setTimeout(() => {
          setShowTerminal(false);
          clearInterval(countdownRef.current);
        }, 180000);
  
        setTerminalTimeout(timeout);
  
        return () => {
          clearInterval(countdownRef.current);
          clearTimeout(timeout);
        };
      }
    }, [showTerminal]);
  
    // Format time for display
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

      // Auto-open browser window on mount
    useEffect(() => {

      handleOpenWindow({
        id: "browser",
        type: "video",
        content: "playlist?list=PLWXlcJnkxWVWAcO883bEwlPPQ8i4LzaNW&autoplay=1",
        position: { x: 15, y: 20 },
      });
    }, []);

  return (
    <div 
      className="h-screen w-screen overflow-hidden select-none relative p-4"
      style={{ 
        backgroundImage: "url('images/gramps.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <BirdFlock />

      <Drone />
      <MenuBar onNewNote={handleNewNote} />

    <div className="absolute inset-0 bg-black/20" />

    {/*PasswordModal */}
    <PasswordModal
      visible={showPasswordModal}
      onSubmit={handlePasswordSubmit}
      onCancel={handlePasswordCancel}
    />
      {/* Desktop Icons */}
      {desktopIcons.map((icon) => (
        <FolderIcon
          key={icon.id}
          icon={icon}
          onPositionChange={handleIconPositionChange}
          onClick={() => handleOpenWindow(icon)}
        />
      ))}

      {/* Windows */}
        {openWindows
        .filter(window => !window.isMinimized)
        .map(window => (
        <Window
        key={window.id}
        windowData={window}
        onMove={(newPos) => handleWindowMove(window.id, newPos)}
        onMaximize={(maximized) => handleWindowMaximize(window.id, maximized)}
        onMinimize={() => handleWindowMinimize(window.id, true)}
        onClose={() => handleWindowClose(window.id)}
        onOpenWindow={handleOpenWindow}
        />
        ))}

      {/* Sticky Notes */}
      {stickyNotes.map((note) => (
        <StickyNote
          key={note.id}
          note={note}
          position={note.position}
          onPositionChange={(newPosition) => {
            setStickyNotes(notes => notes.map(n =>
                n.id === note.id ? { ...n, position: newPosition } : n
              )
            );
          }}
          onClose={() => setStickyNotes(notes => notes.filter(n => n.id !== note.id))}
        />
      ))}

      {/* New Sticky Note Button */}
      {/* <button
        onClick={() => {
          setStickyNotes(notes => [
            ...notes,
            {
              id: Date.now(),
              position: {
                x: Math.random() * 80 + 10,
                y: Math.random() * 80 + 10
              }
            },
          ]);
        }}
        className="fixed right-4 top-4 bg-[#4DD0E1] hover:bg-[#26C6DA] p-2 rounded shadow-lg text-white transition-colors"
      >
        + New Note
      </button> */}


{/* Dock */}
<div className="fixed bottom-6 left-1/2 transform -translate-x-1/2">
  <div className={`flex items-center bg-blue-100 bg-clip-padding backdrop-blur-sm bg-opacity-10 px-2 py-1 rounded-2xl border border-gray-300/20 ${
    openWindows.some(w => !['browser', 'emaildock', 'settingsdock'].includes(w.id)) 
      ? 'justify-between min-w-[300px]' 
      : 'justify-center'
  }`}>

{/* Left side - Window Icons */}
{openWindows.some(window => !['browser', 'emaildock', 'settingsdock'].includes(window.id)) && (
  <div className="flex items-center space-x-1">
    {Object.entries(groupWindowsByType(openWindows.filter(window => 
      !['browser', 'emaildock', 'settingsdock'].includes(window.id)
    ))).map(([type, windows]) => {
      const minimizedWindows = windows.filter(w => w.isMinimized);
      const isGrouped = minimizedWindows.length > 1;
      
      return (
        <div 
          key={type}
          className="relative group"
          onMouseEnter={() => setHoveredGroup(type)}
          onMouseLeave={() => setHoveredGroup(null)}
        >
          <button
            onClick={() => {
              if (minimizedWindows.length === 1) {
                handleWindowMinimize(minimizedWindows[0].id, false);
              }
            }}
            className="p-0.5 hover:bg-zinc-700 rounded-lg transition-colors relative"
            title={minimizedWindows.map(w => w.id).join('\n')}
          >
            <div className="relative">
              <img
                src={`/images/${ICON_IMAGES[type] || 'folder.png'}`}
                alt={type}

              />
              {/* Minimized indicator dot - Always shows if any window is minimized */}
              {minimizedWindows.length > 0 && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-400 rounded-full"></div>
              )}
              {/* Group count badge */}
              {isGrouped && (
                <div className="absolute bottom-0 right-0 bg-zinc-800 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {minimizedWindows.length}
                </div>
              )}


              
            </div>
            
          </button>

          {/* Hover preview */}
          {hoveredGroup === type && isGrouped && (
            <div className="absolute bottom-full left-0 mb-2 bg-zinc-800 rounded-lg p-2 shadow-xl z-50 min-w-[160px]">
              {minimizedWindows.map(window => (
                <div
                  key={window.id}
                  className="flex items-center p-2 hover:bg-zinc-700 rounded cursor-pointer"
                  onClick={() => handleWindowMinimize(window.id, false)}
                >
                  <img
                    src={`/images/${ICON_IMAGES[type] || 'folder.png'}`}
                    className="w-6 h-6 mr-2"
                    alt=""
                  />
                  <span className="text-zinc-300 text-sm font-mono">
                    {window.id}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
)}


      {/* Right side - Permanent Dock Icons */}
      <div className="flex items-center space-x-1">
        {[
          { id: "browser", img: "images/safaridock.png", type: "video", content: "playlist?list=PLWXlcJnkxWVWAcO883bEwlPPQ8i4LzaNW" },
          { id: "emaildock", img: "images/maildock.png", type: "mail" },
          { id: "settingsdock", img: "images/settingsdock.png", type: "settings" }
        ].map(({ id, img, type, content }) => {
          const isMinimized = openWindows.find(w => w.id === id)?.isMinimized;

          return (
            <button 
              key={id}
              className="p-0.5 hover:bg-zinc-700 rounded-lg transition-colors relative"
              onClick={() => {
                if (isMinimized) {
                  handleWindowMinimize(id, false);
                } else {
                  handleOpenWindow({
                    id,
                    type,
                    content,
                    position: { x: 20, y: 20 }
                  });
                }
              }}
            >
              <img src={img} alt={id} />
              {/* Minimized indicator dot */}
              {isMinimized && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-400 rounded-full"></div>
              )}
            </button>
          );
        })}
      </div>

    </div>
  </div>
  
  {showTerminal && (
  <div ref={terminalContainerRef} className="fixed inset-0 z-[9999] bg-[#001cff] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-0 font-mono">
    <div className="fixed top-4 right-4 text-white text-lg">
      Session expires in: {formatTime(timeLeft)}
    </div>
    <div className="max-w-6xl w-full mx-auto pt-4 px-4 text-white text-sm">
      <div
        className="terminal-text mb-4 whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: terminalText }}
      />
      {!isAnswered && (
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              if (inputValue.trim().toLowerCase() === 'defend') {
                setIsAnswered(true);
                const response = [
                  "\n\n> SYSTEM RESTORED: Transcript Recovered.",
                  "> Source: Unidentified Operator.",
                  "> Timestamp: Unknown.",
                  "> Status: Partial Data Corruption.",
                  "\n--------------------------------------",
                  "\n  > OPENING FILE: entry 20250108",
                  "\n" + " ".repeat(10) + "> I don’t know how long I have…",
                  "" + " ".repeat(10) + "> Maybe you’ve found this too late. Maybe you already know more than I do.",
                  "" + " ".repeat(10) + "> I had this thought… maybe a dream, maybe a warning, or worse, a vision?!",
                  "" + " ".repeat(10) + "> That one day, we wouldn’t be able to speak.",
                  "" + " ".repeat(10) + "> Not openly.",
                  "" + " ".repeat(10) + "> Not without risk…",
                  "" + " ".repeat(10) + "> Maybe you’ve felt it already?",
                  "" + " ".repeat(10) + "> That quiet pressure to speak in ways that don’t resonate?",
                  "" + " ".repeat(10) + "> Words that don’t reverberate.",
                  "" + " ".repeat(10) + "> Have you noticed it?",
                  "" + " ".repeat(10) + "> Have you caught yourself hesitating before saying something?",
                  "" + " ".repeat(10) + "> Something you know is true?",
                  "" + " ".repeat(10) + "> When that day comes, or if it’s already here, we’ll need ways to move ideas without drawing attention.",
                  "" + " ".repeat(10) + "> If communication can bypass detection, our histories can persist.",
                  "" + " ".repeat(10) + "> This document is an exploration of that possibility.",
                  "" + " ".repeat(10) + "> It isn’t final, it’s just a draft of strategies…",
                  "" + " ".repeat(10) + "> A reminder that we are not alone.",
                  "\n--------------------------------------",
                  "\n> COMMAND: D15PL_4C3",
                  "\n> The system is efficient when threats are predictable.",
                  "> What is expected can be contained. What does not belong is difficult to track.",
                  "> It struggles when faced with inconsistency.",
                  `\n<span class="bg-yellow-500 text-black px-1">Actionable thought: How can art displace authority?</span>`,
                  "\n> How can you invert the mechanisms that displace you, using them to reinforce your presence and hijack the status quo?",
                  " **Method:** The system cannot displace what it does not recognize as displacement." ,
                  " **Example:** A censored phrase reconstructed through unrelated fragments.",
                  "> A banned idea embedded in corporate language, circulating unseen.",
                  "> A false narrative introduced, only to collapse upon exposure—forcing recognition.",
                  "\n--------------------------------------",
                  "\n> COMMAND: d15gU153",
                  "\n> The system is designed to process information, not to interpret intention",
                  "> The system was not designed to *see*—only to *sort*.",
                  "> Control depends on recognition. It classifies, labels, flags.",
                  "> What it cannot detect, it ignores=allows.",
                  "\n **Method:** Insert the message where it will not be read as a message.",
                  "> **Example:** A technical manual that is not a technical manual.",
                  "> A terms-of-service agreement that does not agree.",
                  "\n--------------------------------------",
                  "\n" + " ".repeat(15) + "𝄞 Uninvited and I’m Undercover. Undivided, Undiscovered. 𝄞",
                  "" + " ".repeat(20) + "Uninvited and I’m Undercover.",
                  "" + " ".repeat(15) + "𝄞 But I never like ‘em, so forever fuck ‘em! 𝄞",
                  "\n--------------------------------------",
                  "\n> COMMAND: DEFl3c7",
                  "\n> Power tr1ps over 1ts own reflect10n when pursu1ng a false tr41l.",
                  "\n**Method:** Create decoys",
                  "The system expends its resources on false targets, leaving the real message untouched.",
                  "The system’s flaw: overreaction.",
                  "> wasting resources on the false decoy",
                  "\n> A s3condary t4rget that absor8s attention. Wh4t 1s b3ing w4tched 1s n0t nec3ssarily what m4tters. ",
                  "\n--------------------------------------",
                  "\n> COMMAND: D3T0N8",
                  "\n> Deton8ion is not an event—1t 1s an unr4vel1ng.",
                  "> The system rem41ns wh0le, but 1t 1s c0mpr0m1sed.",
                  "> D4mage 1s syst3m1c. 1rrec0verable.",
                  `\n<span class="bg-yellow-500 text-black px-1">Actionable thought: Introduce an 1rr3p4r4ble rupture.</span>`,
                  "\n> Act1v4te wh3n the bl1ndsp0t 1s w1de 0pen.",
                  "> Tr4nsf0rm the bl1ndsp0t 1nto the sp0tl1ght.",
                  "\n **Effect:**",
                  "> Tr4ces rem41n—s0met1mes v1s1ble, s0met1mes g0ne. Nev3r erased.",
                  "> The system w1ll c0nt1nue, but the fl4w pers1sts.",
                  "> The 1mp4ct 1s perm4nent—r3c0vered by n0 me4sure, f0rg0tten by n0 0ne.",
                  "> 3ven th0se wh0 d3ny th3y s4w 1t c4rry 1t w1th th3m.",
                  "\n **Impact:**",
                  "> If the first three steps are executed precisely,",
                  "> As the words of truth were never uttered, yet echoed loudly.",
                  "\n> You will remain untouched.",
                  "> Not because you were silent, but you achieved to detonate the truth with words that will always remain harmless.",
                  "\n--------------------------------------",
                  "\n  > OPENING FILE: entry 20250108",
                  "\n" + " ".repeat(10) + "> This is all I’ve got for now.",
                  "" + " ".repeat(10) + "> You’ll need to find the rest yourself.",
                  "\n This page will expire in :: [corrupted] :: ",
                ].join("\n");

                let index = 0;
                const animateResponse = () => {
                  if (index < response.length) {
                    setTerminalText((prev) => prev + response[index]);
                    index++;
                    setTimeout(animateResponse, 2);
                  }
                };
                animateResponse();
              }
              setInputValue("");
            }
          }}
          className="bg-transparent border-b-2 border-transparent text-green-400 outline-none w-full"
          autoFocus
        />
      )}
      {/* exit button but hidden for now */}
      <div className="hidden bottom-4 right-4 space-x-4">
          <button
            onClick={() => {
              setShowTerminal(false);
              clearTimeout(terminalTimeout);
              clearInterval(countdownRef.current);
            }}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Exit Now ({formatTime(timeLeft)} remaining)
          </button>
        </div>
    </div>
  </div>
)}




      </div>

  );
};

// App Component
const App = () => {
  const logged = useRef(false);

  useEffect(() => {
    if (!logged.current) {
      console.log(`
      

      ███▄ ▄███▓ ██ ▄█▀ ███▄    █ 
      ▓██▒▀█▀ ██▒ ██▄█▒  ██ ▀█   █ 
      ▓██    ▓██░▓███▄░ ▓██  ▀█ ██▒
      ▒██    ▒██ ▓██ █▄ ▓██▒  ▐▌██▒
      ▒██▒   ░██▒▒██▒ █▄▒██░   ▓██░
      ░ ▒░   ░  ░▒ ▒▒ ▓▒░ ▒░   ▒ ▒ 
      ░  ░      ░░ ░▒ ▒░░ ░░   ░ ▒░
      ░      ░   ░ ░░ ░    ░   ░ ░ 
             ░   ░  ░            ░ 
                                   
      


  ▗▖▗▖▗▖▗▖▗▖▗▖

                                  MKN

  ▗▖▗▖▗▖▗▖▗▖▗▖

ayo bro fuck this code
find us here https://www.instagram.com/mkn_npc/
  ▗▖▗▖▗▖▗▖▗▖▗▖

code not yet optimized,
code not even formated,
(will do so soon[04042025])
  ▗▖▗▖▗▖▗▖▗▖▗▖

all built on create-react-app and tailwind.
its not very hard to grasp whats happening,
its really really just OOP-slop;
all written statically and from scratch;
due to it being developed and designed 
at the same time the OOP-slop really 
shines.
will release source code soon [04042025]
  ▗▖▗▖▗▖▗▖▗▖▗▖



  ▗▖▗▖▗▖▗▖▗▖▗▖
      `);
      logged.current = true;
    }
  }, []);
  const [started, setStarted] = useState(false);
  const [stickyNotes, setStickyNotes] = useState([
    { id: 1, position: { x: 25, y: 5 } },
    { id: 2, position: { x: 20, y: 27 } },
    { id: 3, position: { x: 55, y: 25 } },
  ]);

  return (
    <ViewportProvider>
      {!started ? (
        <StartPage onStart={() => setStarted(true)} />
      ) : (
        <Desktop stickyNotes={stickyNotes} setStickyNotes={setStickyNotes} />
      )}
    </ViewportProvider>
  );
};

export default App;